import React, { createContext, useContext, useMemo, useState } from "react";

/* util */
const cx = (...a) => a.filter(Boolean).join(" ");

/* CARD */
export function Card({ className = "", children }) {
  return <div className={cx("rounded-lg border bg-white dark:bg-neutral-900 shadow-sm", className)}>{children}</div>;
}
export function CardHeader({ className = "", children }) {
  return <div className={cx("p-4 border-b border-gray-200 dark:border-neutral-800", className)}>{children}</div>;
}
export function CardTitle({ className = "", children }) {
  return <h3 className={cx("text-lg font-semibold", className)}>{children}</h3>;
}
export function CardDescription({ className = "", children }) {
  return <p className={cx("text-sm text-gray-500 dark:text-neutral-400", className)}>{children}</p>;
}
export function CardContent({ className = "", children }) {
  return <div className={cx("p-4", className)}>{children}</div>;
}

/* BUTTON */
const BTN_VAR = {
  default: "bg-blue-600 text-white hover:bg-blue-700",
  secondary: "bg-gray-200 dark:bg-neutral-700 text-black dark:text-white hover:opacity-90",
  outline: "border border-gray-300 dark:border-neutral-700 hover:bg-gray-50 dark:hover:bg-neutral-800",
  ghost: "hover:bg-gray-100 dark:hover:bg-neutral-800",
};
const BTN_SZ = { sm: "h-8 px-2 text-sm", md: "h-9 px-3", lg: "h-10 px-4" };

export function Button({ variant = "default", size = "md", className = "", children, ...props }) {
  return (
    <button
      className={cx("inline-flex items-center justify-center rounded-md font-medium transition", BTN_VAR[variant], BTN_SZ[size], className)}
      {...props}
    >
      {children}
    </button>
  );
}

/* INPUT */
export const Input = React.forwardRef(function Input({ className = "", ...props }, ref) {
  return (
    <input
      ref={ref}
      className={cx(
        "h-9 w-full rounded-md border border-gray-300 dark:border-neutral-700",
        "bg-white dark:bg-neutral-900 px-3 outline-none focus:ring-2 focus:ring-blue-500",
        className
      )}
      {...props}
    />
  );
});

/* BADGE */
export function Badge({ className = "", children, variant = "default" }) {
  const v = variant === "outline" ? "border-gray-300 dark:border-neutral-700" : "bg-gray-200 dark:bg-neutral-700 border-transparent";
  return <span className={cx("inline-flex items-center rounded border px-2 py-0.5 text-xs", v, className)}>{children}</span>;
}

/* SEPARATOR */
export function Separator({ className = "" }) {
  return <div className={cx("h-px w-full bg-gray-200 dark:bg-neutral-700", className)} />;
}

/* TABS (controlled/uncontrolled) */
const TabsCtx = createContext(null);

export function Tabs({ value, onValueChange, defaultValue = "All", children }) {
  const [internal, setInternal] = useState(defaultValue);
  const controlled = value !== undefined;
  const ctx = useMemo(
    () => ({
      value: controlled ? value : internal,
      setValue: (v) => (controlled ? onValueChange?.(v) : setInternal(v)),
    }),
    [controlled, value, internal, onValueChange]
  );
  return <TabsCtx.Provider value={ctx}><div>{children}</div></TabsCtx.Provider>;
}

export function TabsList({ className = "", children }) {
  return <div className={cx("inline-flex gap-2", className)}>{children}</div>;
}

export function TabsTrigger({ value, children }) {
  const ctx = useContext(TabsCtx);
  const active = ctx?.value === value;
  return (
    <button
      className={cx(
        "h-9 px-3 rounded border",
        active ? "bg-blue-600 text-white border-blue-600" : "border-gray-300 dark:border-neutral-700"
      )}
      onClick={() => ctx?.setValue(value)}
    >
      {children}
    </button>
  );
}

export function TabsContent({ value, className = "", children }) {
  const ctx = useContext(TabsCtx);
  if (ctx?.value !== value) return null;
  return <div className={className}>{children}</div>;
}

/* DROPDOWN (minimal) */
export function DropdownMenu({ children }) { return <div className="relative inline-block">{children}</div>; }

export function DropdownMenuTrigger({ asChild, children, ...props }) {
  const child = React.Children.only(children);
  return React.cloneElement(child, { ...props });
}

export function DropdownMenuContent({ className = "", children }) {
  // Minimalno: bez portala/pozicioniranja
  return <div className={cx("mt-2 min-w-48 rounded-md border bg-white dark:bg-neutral-900 shadow p-1", className)}>{children}</div>;
}

export function DropdownMenuLabel({ children, className = "" }) {
  return <div className={cx("px-2 py-1.5 text-xs font-semibold opacity-70", className)}>{children}</div>;
}
export function DropdownMenuSeparator() { return <div className="my-1 h-px bg-gray-200 dark:bg-neutral-700" />; }

export function DropdownMenuItem({ onClick, children, className = "" }) {
  return (
    <button onClick={onClick} className={cx("w-full text-left px-2 py-1.5 rounded hover:bg-gray-100 dark:hover:bg-neutral-800", className)}>
      {children}
    </button>
  );
}

/* AVATAR */
export function Avatar({ className = "", children }) {
  return <div className={cx("h-8 w-8 rounded-full bg-gray-200 dark:bg-neutral-700 flex items-center justify-center", className)}>{children}</div>;
}
export function AvatarFallback({ children }) {
  return <span className="text-xs font-medium">{children}</span>;
}
